/**
 * LEGACY_EDITOR — le document qu'édite l'ancien éditeur, et son brouillon (plan
 * document-drafts). L'éditeur ne montre qu'une page ; le document entier reste ici, et seule
 * cette page reçoit les calques du canvas à l'enregistrement — les autres ne bougent pas.
 *
 * Trois requêtes, celles du nouvel éditeur (DocumentsController, Documents::CommitsController,
 * le destroy du porteur) : PATCH du document, POST du commit, DELETE du brouillon. Le serveur
 * décide où aller : sa réponse JSON porte `Location` ; COMMENT y aller est à l'hôte
 * (legacy_fabric_controller : fermer la modale, ou visiter).
 */
interface Slide {
  id?: string
  layers?: object[]
  [key: string]: unknown
}

interface CompositionDocument {
  slides?: Slide[]
  [key: string]: unknown
}

interface LegacyDocumentOpts {
  doc: CompositionDocument
  slideIndex: number
  documentUrl?: string
  commitUrl?: string
  discardUrl?: string
}

export class LegacyDocument {
  readonly doc: CompositionDocument
  readonly slideIndex: number
  private readonly documentUrl?: string
  private readonly commitUrl?: string
  private readonly discardUrl?: string

  constructor({ doc, slideIndex, documentUrl, commitUrl, discardUrl }: LegacyDocumentOpts) {
    this.doc = doc;
    this.slideIndex = slideIndex;
    this.documentUrl = documentUrl;
    this.commitUrl = commitUrl;
    this.discardUrl = discardUrl;
  }

  get page(): Slide | undefined {
    return this.doc.slides?.[this.slideIndex];
  }

  /** Les calques de la page éditée, tels que le document les porte. */
  layers(): object[] {
    return this.page?.layers || [];
  }

  /** La page reçoit les calques du canvas ; le document part en base. */
  async save(layers: object[]): Promise<boolean> {
    const page = this.page;
    if (!page || !this.documentUrl) return false;

    page.layers = layers;
    const response = await fetch(this.documentUrl, {
      method: "PATCH",
      headers: { ...this.headers(), "Content-Type": "application/json" },
      body: JSON.stringify(this.doc),
    });
    return response.ok;
  }

  /** Le commit : rend `Location` (où aller), null en cas d'échec. */
  commit(): Promise<string | null | false> {
    return this.commitUrl ? this.request("POST", this.commitUrl) : Promise.resolve(false);
  }

  /** Quitter : le brouillon se jette. Rend `Location`, null en cas d'échec. */
  discard(): Promise<string | null | false> {
    return this.discardUrl ? this.request("DELETE", this.discardUrl) : Promise.resolve(false);
  }

  private async request(method: string, url: string): Promise<string | null | false> {
    const response = await fetch(url, { method, headers: this.headers() });
    if (!response.ok) return false;

    return response.headers.get("Location");
  }

  private headers(): Record<string, string> {
    const token = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
    return { Accept: "application/json", ...(token ? { "X-CSRF-Token": token } : {}) };
  }
}
